import React, { useMemo, useState } from "react"
import { MdAdd, MdCancel, MdDeleteOutline, MdDescription, MdEmail, MdOpenInNew, MdOutlineAttachMoney, MdOutlineSearch, MdSend } from "react-icons/md"
import { toast } from "react-toastify"
import Modal from "../index"
import ModalConfirm from "../ModalConfirm"
import ModalDelete from "../ModalDelete"
import ModalNewTransaction from "./ModalNewTransaction"
import TransactionActionsMenu from "./TransactionActionsMenu"
import ModalFinancialEmail from "./ModalFinancialEmail"
import ModalFinancialEmailHistory from "./ModalFinancialEmailHistory"
import Button from "../../Form/Button"
import Input from "../../Form/Input"
import Loading from "../../../pages/layouts/Loading"
import api from "../../../services/api"
import { formatCurrency, maskDate } from "../../../utils/formatter"
import { getOptionByOptionList, openLinkExternal } from "../../../utils/utils"
import styles from "./modalTransactions.module.css"

const listTypes = [
    { id: "SUBSCRIPTION", value: "Mensalidade" },
    { id: "CUSTOM_SERVICE", value: "Serviço ou cobrança avulsa" },
    { id: "VOIP", value: "Créditos VOIP" },
    { id: "CHAT", value: "Créditos Chat" },
    { id: "PARTNER_PAYMENT", value: "Repasse mensal de parceiro" },
    { id: "ADDITIONAL_USERS", value: "Usuários adicionais" },
    { id: "ADDITIONAL_LEADS", value: "Base adicional de leads" },
    { id: "PLAN_CHANGE", value: "Troca de plano" },
]

const listStatus = [
    { id: "ALL", value: "Todos" },
    { id: "PENDING", value: "Pendente" },
    { id: "ISSUED", value: "Em aberto" },
    { id: "APPROVED", value: "Pago" },
    { id: "RECUSED", value: "Recusado" },
    { id: "CANCELED", value: "Cancelado" },
]

const paymentMethods = [
    { id: "BANK_SLIP", value: "Boleto / PIX" },
    { id: "CREDIT_CARD", value: "Cartão de crédito" },
    { id: "DEBIT_CARD", value: "Cartão de débito" },
    { id: "PIX", value: "PIX" },
    { id: "BANK_TRANSFER", value: "Transferência bancária" },
    { id: "CASH", value: "Dinheiro" },
    { id: "OTHER", value: "Outro meio" },
]

const operationLabels = {
    CHARGE: "Cobrança emitida",
    REGISTER_PAYMENT: "Recebimento externo",
    INVOICE_ONLY: "Somente Nota Fiscal",
}

const ModalTransactions = ({ value = [], account_id, closeModal = () => { } }) => {
    const [transactions, setTransactions] = useState(Array.isArray(value) ? value : [])
    const [modalNewTransaction, setModalNewTransaction] = useState(false)
    const [modalDelete, setModalDelete] = useState(false)
    const [modalCancel, setModalCancel] = useState(false)
    const [transactionIdDelete, setTransactionIdDelete] = useState("")
    const [transactionIdCancel, setTransactionIdCancel] = useState("")
    const [emailAction, setEmailAction] = useState(null)
    const [emailHistoryTransactionId, setEmailHistoryTransactionId] = useState("")
    const [statusFilter, setStatusFilter] = useState("ALL")
    const [search, setSearch] = useState("")
    const [loading, setLoading] = useState(false)

    const updateTransaction = updated => setTransactions(previous => previous.map(item => item._id === updated?._id ? { ...item, ...updated } : item))
    const netValue = transaction => Math.max(0, Number(transaction?.value || 0) - Number(transaction?.discount || 0))
    const displayDate = date => date ? maskDate(date) : "-"

    const summary = useMemo(() => {
        const approved = transactions.filter(item => item.status === "APPROVED")
        const open = transactions.filter(item => ["PENDING", "ISSUED"].includes(item.status))
        return {
            received: approved.reduce((total, item) => total + netValue(item), 0),
            open: open.reduce((total, item) => total + netValue(item), 0),
            invoices: transactions.filter(item => item.link_invoice).length,
            total: transactions.length,
        }
    }, [transactions])

    const filteredTransactions = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase()
        return [...transactions]
            .filter(item => statusFilter === "ALL" || item.status === statusFilter)
            .filter(item => !normalizedSearch || [item.observation, item.metadata?.external_reference, getOptionByOptionList(item.type, listTypes)].some(text => `${text || ""}`.toLowerCase().includes(normalizedSearch)))
            .sort((left, right) => new Date(right.payday || right.due_date || right.created_at) - new Date(left.payday || left.due_date || left.created_at))
    }, [transactions, statusFilter, search])

    const handleSaveNewTransaction = transaction => {
        setLoading(true)
        api.post(`/accounts/${account_id}/transactions`, transaction)
            .then(response => {
                if (response.data?.data) setTransactions(previous => [response.data.data, ...previous])
                response.data?.warning ? toast.warning(response.data.message) : toast.success(response.data.message)
                setModalNewTransaction(false)
            })
            .catch(err => toast.error(err?.response?.data?.message || "Não foi possível criar o lançamento."))
            .finally(() => setLoading(false))
    }

    const deleteTransaction = id => {
        setLoading(true)
        api.delete(`/accounts/${account_id}/transactions/${id}`)
            .then(response => {
                setTransactions(previous => previous.filter(item => item._id !== id))
                toast.success(response.data.message)
                setModalDelete(false)
            })
            .catch(err => toast.error(err?.response?.data?.message))
            .finally(() => setLoading(false))
    }

    const cancelTransaction = () => {
        setLoading(true)
        api.patch(`/accounts/${account_id}/transactions/${transactionIdCancel}/cancel`)
            .then(response => {
                updateTransaction(response.data?.data)
                toast.success(response.data.message)
                setModalCancel(false)
            })
            .catch(err => toast.error(err?.response?.data?.message))
            .finally(() => setLoading(false))
    }

    const emitInvoice = id => {
        setLoading(true)
        api.post(`/accounts/${account_id}/transactions/${id}/invoice/emit`)
            .then(response => {
                updateTransaction(response.data?.data)
                toast.success(response.data.message)
            })
            .catch(err => toast.error(err?.response?.data?.message || "Não foi possível emitir a Nota Fiscal."))
            .finally(() => setLoading(false))
    }

    const emitCharge = id => {
        setLoading(true)
        api.post(`/accounts/${account_id}/transactions/${id}/charge/emit`)
            .then(response => {
                updateTransaction(response.data?.data)
                toast.success(response.data.message)
            })
            .catch(err => toast.error(err?.response?.data?.message || "Não foi possível emitir a cobrança."))
            .finally(() => setLoading(false))
    }

    const sendFinancialEmail = recipients => {
        if (!emailAction) return
        setLoading(true)
        api.post(`/accounts/${account_id}/transactions/${emailAction.transactionId}/${emailAction.documentType}/send`, { recipients })
            .then(response => {
                updateTransaction(response.data?.data)
                response.data?.warning ? toast.warning(response.data.message) : toast.success(response.data.message)
                setEmailAction(null)
            })
            .catch(err => toast.error(err?.response?.data?.message || "Não foi possível enviar o documento."))
            .finally(() => setLoading(false))
    }

    return <>
        <Modal icon={<MdOutlineAttachMoney />} title="Financeiro da conta" description="Cobranças, recebimentos externos e documentos fiscais em um único histórico." closeModal={closeModal} fullWidith={true} bodyClassName={styles.modalBody}>
            <div className={styles.content}>
                <section className={styles.summaryGrid}>
                    <div><small>Recebido</small><strong>{formatCurrency(summary.received)}</strong><span>Pagamentos confirmados</span></div>
                    <div><small>Em aberto</small><strong>{formatCurrency(summary.open)}</strong><span>Pendente ou emitido</span></div>
                    <div><small>Notas emitidas</small><strong>{summary.invoices}</strong><span>Documentos disponíveis</span></div>
                    <div><small>Lançamentos</small><strong>{summary.total}</strong><span>Histórico completo</span></div>
                </section>

                <div className={styles.toolbar}>
                    <div className={styles.searchBox}><MdOutlineSearch /><Input type="search" placeholder="Buscar descrição ou referência" value={search} onChange={event => setSearch(event.target.value)} /></div>
                    <div className={styles.statusFilters}>
                        {listStatus.map(status => <button type="button" key={status.id} className={statusFilter === status.id ? styles.activeFilter : ""} onClick={() => setStatusFilter(status.id)}>{status.value}</button>)}
                    </div>
                    <Button variant="primary" fullWidth={false} onClick={() => setModalNewTransaction(true)}><MdAdd /> Novo lançamento</Button>
                </div>

                <section className={styles.transactionList}>
                    {!filteredTransactions.length && <div className={styles.empty}>Nenhum lançamento encontrado para os filtros selecionados.</div>}
                    {filteredTransactions.map(transaction => {
                        const statusLabel = getOptionByOptionList(transaction.status, listStatus) || transaction.status
                        const operation = operationLabels[transaction.metadata?.operation_mode]
                        const actions = [
                            transaction.charge_status === "PENDING" ? { label: "Emitir cobrança", icon: <MdOutlineAttachMoney />, onClick: () => emitCharge(transaction._id) } : null,
                            transaction.charge_status === "OPENED" && transaction.link_charge ? { label: "Abrir cobrança", icon: <MdOpenInNew />, onClick: () => openLinkExternal(transaction.link_charge) } : null,
                            transaction.charge_status === "OPENED" ? { label: "Enviar cobrança", icon: <MdSend />, onClick: () => setEmailAction({ transactionId: transaction._id, documentType: "charge" }) } : null,
                            transaction.link_invoice ? { label: "Abrir Nota Fiscal", icon: <MdOpenInNew />, onClick: () => openLinkExternal(transaction.link_invoice) } : null,
                            !transaction.link_invoice && transaction.status !== "CANCELED" ? { label: "Emitir Nota Fiscal", icon: <MdDescription />, onClick: () => emitInvoice(transaction._id) } : null,
                            transaction.link_invoice ? { label: "Enviar Nota Fiscal", icon: <MdSend />, onClick: () => setEmailAction({ transactionId: transaction._id, documentType: "invoice" }) } : null,
                            transaction.metadata?.email_delivery_ids?.length ? { label: "Histórico de envios", icon: <MdEmail />, onClick: () => setEmailHistoryTransactionId(transaction._id) } : null,
                            !["APPROVED", "CANCELED"].includes(transaction.status) ? { label: "Cancelar cobrança", icon: <MdCancel />, danger: true, onClick: () => { setTransactionIdCancel(transaction._id); setModalCancel(true) } } : null,
                            transaction.status !== "APPROVED" ? { label: "Excluir registro", icon: <MdDeleteOutline />, danger: true, onClick: () => { setTransactionIdDelete(transaction._id); setModalDelete(true) } } : null,
                        ].filter(Boolean)
                        return <article className={styles.transactionCard} key={transaction._id}>
                            <div className={styles.transactionMain}>
                                <div className={styles.transactionIcon}><MdOutlineAttachMoney /></div>
                                <div className={styles.transactionIdentity}>
                                    <div><strong>{getOptionByOptionList(transaction.type, listTypes) || transaction.type}</strong><span className={`${styles.statusBadge} ${styles[`status${transaction.status}`] || ""}`}>{statusLabel}</span></div>
                                    <span>{operation || "Registro financeiro"}{transaction.metadata?.external_reference ? ` · Ref. ${transaction.metadata.external_reference}` : ""}</span>
                                    {transaction.observation && <p>{transaction.observation}</p>}
                                </div>
                            </div>
                            <div className={styles.transactionFacts}>
                                <div><small>Total</small><strong>{formatCurrency(netValue(transaction))}</strong>{Number(transaction.discount) > 0 && <span>Bruto {formatCurrency(transaction.value)} · desconto {formatCurrency(transaction.discount)}</span>}</div>
                                <div><small>{transaction.status === "APPROVED" ? "Recebido em" : "Vencimento"}</small><strong>{displayDate(transaction.status === "APPROVED" ? transaction.payday : transaction.due_date)}</strong><span>{getOptionByOptionList(transaction.method, paymentMethods) || "-"}</span></div>
                                <div><small>Nota Fiscal</small><strong>{transaction.number_nf ? `NF ${transaction.number_nf}` : transaction.invoice_status === "ERROR" ? "Erro na emissão" : "Não emitida"}</strong><span>{transaction.link_invoice ? "Documento disponível" : "Pode ser emitida pelo histórico"}</span></div>
                            </div>
                            <TransactionActionsMenu actions={actions} />
                        </article>
                    })}
                </section>
            </div>
        </Modal>

        {modalNewTransaction && <ModalNewTransaction listTypes={listTypes} onSubmit={handleSaveNewTransaction} closeModal={() => setModalNewTransaction(false)} />}
        {emailAction && <ModalFinancialEmail accountId={account_id} documentType={emailAction.documentType} onSubmit={sendFinancialEmail} closeModal={() => setEmailAction(null)} />}
        {emailHistoryTransactionId && <ModalFinancialEmailHistory accountId={account_id} transactionId={emailHistoryTransactionId} closeModal={() => setEmailHistoryTransactionId("")} />}
        {modalCancel && <ModalConfirm closeModal={() => setModalCancel(false)} onSubmit={cancelTransaction} icon={<MdCancel />} title="Cancelar cobrança" description="Confirma o cancelamento desta cobrança?" text="O link ou boleto deixará de aceitar pagamentos. O registro permanecerá no histórico financeiro." />}
        {modalDelete && <ModalDelete closeModal={() => setModalDelete(false)} onSubmit={deleteTransaction} icon={<MdDeleteOutline />} title="Excluir registro" description="Deseja realmente excluir este registro financeiro?" id={transactionIdDelete} />}
        {loading && <Loading fullPage={true} />}
    </>
}

export default ModalTransactions
